import { useState } from 'react';
import { Link } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import Modal from '../components/Modal';
import { trainingCentersAPI } from '../services/api';
import useTrainingCenters from '../hooks/useTrainingCenters';

const EMPTY_FORM = { name: '', code: '', isActive: true };

export default function TrainingCentersPage() {
  const { centers, loading, error, refresh } = useTrainingCenters({ includeInactive: true });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
    setMessage('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      code: row.code || '',
      isActive: row.isActive !== false,
    });
    setShowModal(true);
    setMessage('');
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      if (editing) {
        await trainingCentersAPI.update(editing.id, form);
      } else {
        await trainingCentersAPI.create(form);
      }
      setShowModal(false);
      await refresh();
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Could not save location');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Hide “${row.name}” from new dropdowns? Existing batches keep this label.`)) {
      return;
    }
    try {
      await trainingCentersAPI.remove(row.id);
      await refresh();
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Could not hide location');
    }
  };

  return (
    <StaffPageShell
      title="Training centers"
      subtitle="One Location of Center list for Add User, batches, courses, and filters. Renaming updates batches that use the old label."
    >
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
        Student address (country / state / city) is separate.{' '}
        <Link to="/admin/masters" className="underline">
          Master data
        </Link>
      </p>

      {error ? (
        <p className="mb-3 text-sm" style={{ color: 'var(--color-error)' }}>
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="mb-3 text-sm" style={{ color: 'var(--color-error)' }}>
          {message}
        </p>
      ) : null}

      <div className="mb-4">
        <button
          type="button"
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-white font-medium"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          Add location
        </button>
      </div>

      {loading ? (
        <p className="theme-text">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
          <table className="min-w-full text-sm">
            <thead style={{ backgroundColor: 'var(--color-surface)' }}>
              <tr className="text-left theme-text">
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">ID letter</th>
                <th className="px-3 py-2">Batches</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {centers.length === 0 ? (
                <tr>
                  <td className="px-3 py-3 theme-text-secondary" colSpan={5}>
                    No locations yet. Add one, or create an offline batch — that name is registered here.
                  </td>
                </tr>
              ) : (
                centers.map((row) => (
                  <tr key={row.id} className="border-t theme-text" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="px-3 py-2 font-medium">{row.name}</td>
                    <td className="px-3 py-2 font-mono">{row.code || '—'}</td>
                    <td className="px-3 py-2">{row.usageCount ?? 0}</td>
                    <td className="px-3 py-2">{row.isActive ? 'Active' : 'Hidden'}</td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button type="button" className="underline" onClick={() => openEdit(row)}>
                        Edit
                      </button>
                      {row.isActive ? (
                        <button type="button" className="underline" onClick={() => deactivate(row)}>
                          Hide
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit location' : 'Add location'}
        size="sm"
      >
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1 theme-text">Location of Center *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg theme-input"
              placeholder="e.g., Noida"
              maxLength={80}
            />
            {editing ? (
              <p className="text-xs mt-1 theme-text-secondary">
                Changing the name updates batches and course offline centers that use the current label.
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 theme-text">Student ID letter</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg theme-input"
              placeholder="N, M, B, C, R…"
              maxLength={4}
            />
            <p className="text-xs mt-1 theme-text-secondary">
              Offline IDs use this letter (OFN26PO0001). Leave blank to infer from the name.
            </p>
          </div>
          {editing ? (
            <label className="flex items-center gap-2 text-sm theme-text">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active in dropdowns
            </label>
          ) : null}
          {message ? (
            <p className="text-sm" style={{ color: 'var(--color-error)' }}>
              {message}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-3 py-2 rounded-lg" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </StaffPageShell>
  );
}
